import {
  Alert,
  Anchor,
  Box,
  Button,
  Center,
  List,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconCircleCheck, IconMail } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/http';

/**
 * Pantalla post-signup del backoffice (Sprint 5.1): "revisa tu email".
 *
 * Se muestra justo despues de que el gestor termina el wizard de signup.
 * El backend ya ha enviado el email de verificacion al usuario, asi que
 * esta pagina NO tiene que pedir email (viene en `location.state.email`
 * que pasa `Signup.tsx` al hacer navigate). Aun asi, si el usuario
 * llega aqui sin state (por ejemplo, recargando la pagina), le dejamos
 * escribirlo para que el boton de reenviar siga siendo util.
 *
 * Contenido (mismo patron que el resto de pantallas de auth del backoffice:
 * tarjeta blanca sobre fondo `deca.0`, IconMail + Title c="deca.9").
 */
export function VerificaTuEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const emailFromState = (location.state as { email?: string } | null)?.email ?? '';

  const [email, setEmail] = useState(emailFromState);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  // Si el state trae el email, mantenemos el input sincronizado y
  // disabled para que se vea claro a donde se ha enviado el email.
  // Si no trae nada, dejamos que el usuario lo escriba.
  useEffect(() => {
    setEmail(emailFromState);
  }, [emailFromState]);

  const handleResend = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return;
    setResendStatus('sending');
    try {
      await apiClient.post('/auth/resend-verification', {
        email: email.trim().toLowerCase(),
      });
      setResendStatus('sent');
    } catch {
      // Anti-enumeracion: el backend ya responde 200 aunque el email
      // no exista. Si llegamos aqui es un error de transporte real, asi
      // que lo marcamos para que el usuario sepa que lo intente de nuevo.
      setResendStatus('error');
    }
  };

  return (
    <Center mih="100vh" bg="deca.0" px="md">
      <Box
        p="xl"
        bg="white"
        w="100%"
        maw={520}
        style={{ borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
      >
        <Stack gap="md">
          <Stack align="center" gap={6}>
            <IconMail size={36} color="var(--mantine-color-deca-9)" />
            <Title order={2} c="deca.9" ta="center">
              Verifica tu email
            </Title>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" ta="center">
              Para completar tu registro, necesitas confirmar tu direccion de correo electronico.
            </Text>
            <Text size="sm" ta="center">
              Te hemos enviado un email con un enlace de verificacion. Abre el mensaje y pulsa{' '}
              <Text span fw={600}>
                &quot;Verificar mi email&quot;
              </Text>
              .
            </Text>
          </Stack>

          {emailFromState && (
            <Text size="xs" c="dimmed" ta="center">
              Email enviado a{' '}
              <Text span fw={600}>
                {emailFromState}
              </Text>
            </Text>
          )}

          <Box
            p="md"
            style={{
              border: '1px solid var(--mantine-color-gray-2)',
              borderRadius: 8,
              backgroundColor: 'var(--mantine-color-gray-0)',
            }}
          >
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                ¿No has recibido el email?
              </Text>
              <List size="sm" spacing={2}>
                <List.Item>Revisa la carpeta de spam o correo no deseado.</List.Item>
                <List.Item>Comprueba que la direccion de email sea correcta.</List.Item>
                <List.Item>Puedes solicitar un nuevo email de verificacion.</List.Item>
              </List>
            </Stack>
          </Box>

          {!emailFromState && (
            <TextInput
              label="Tu email"
              placeholder="tu@empresa.com"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.currentTarget.value);
                if (resendStatus !== 'idle') setResendStatus('idle');
              }}
              required
            />
          )}

          {resendStatus === 'sent' ? (
            <Alert color="green" variant="light" icon={<IconCircleCheck size={20} />}>
              Si el email existe, te hemos enviado un nuevo enlace de verificacion. Revisa tu
              bandeja (y la carpeta de spam).
            </Alert>
          ) : (
            <Button
              color="deca.9"
              fullWidth
              size="md"
              leftSection={<IconMail size={18} />}
              loading={resendStatus === 'sending'}
              onClick={handleResend}
              disabled={!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())}
            >
              Reenviar email de verificacion
            </Button>
          )}

          {resendStatus === 'error' && (
            <Text size="xs" c="red" ta="center">
              No hemos podido enviar el email. Intentalo de nuevo en unos segundos.
            </Text>
          )}

          <Text size="sm" c="dimmed" ta="center">
            ¿Ya has verificado?{' '}
            <Anchor component={Link} to="/login" fw={600}>
              Volver al inicio de sesion
            </Anchor>
          </Text>

          {/* Enlace discreto para volver al wizard por si el usuario quiere
              rehacer el registro (mismo email, nueva password, etc). */}
          <Text size="xs" c="dimmed" ta="center">
            ¿Te has equivocado al registrarte?{' '}
            <Anchor component="button" type="button" onClick={() => navigate('/signup')}>
              Volver al registro
            </Anchor>
          </Text>
        </Stack>
      </Box>
    </Center>
  );
}

export default VerificaTuEmail;
